import { EOrganization, ESnapshotExists } from 'lib/types';
import { useBooleanFlag } from 'utils/flags';
import { LaunchDarklyFlags } from 'lib/types/launchDarklyFlags';
import { TextField } from 'lib/components/TextField';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { SearchableOrganizationRecord } from 'lib/types/searchable';
import { LoadingSpinner } from 'lib/components/LoadingSpinner';
import { ColumnButton } from 'lib/components/ColumnButton';
import { OrganizationModel } from 'lib/model/objects/organizationModel';
import { getModelFromSnapshot } from 'lib/model';
import { Badge } from 'lib/components/Badge';
import { getFirebaseContext } from 'utils/firebase';
import { getNewspaperLogo } from 'routes/ads/place/helpers/newspapers';
import CheckboxInput from 'lib/components/Checkbox/CheckboxInput';
import {
  formatOptions,
  getPublisherLocationDescription
} from './SelectPublisherDropdown';
import {
  getLocationFilterCount,
  PublisherLocationFilter
} from './PublisherLocationFilter';
import { AdjudicationFilters, PublisherFilters } from '../helpers';

type FastConfirmPublisherFormProps = {
  publisherOrganization: ESnapshotExists<EOrganization> | undefined;
  onPublisherOrganizationChange: (publisherOrganizationId: string) => void;
  searchText: string;
  setSearchText: (text: string) => void;
  publisherOptions: SearchableOrganizationRecord[];
  stateOptions: number[];
  onStateChange: (stateId?: number) => void;
  onAdjudicationFilterChange: (filters: AdjudicationFilters) => void;
  activeFilters: PublisherFilters;
  loading: boolean;
  columnCertifiedFilter: boolean;
  setColumnCertifiedFilter: (value: boolean) => void;
};

export default function FastConfirmPublisherForm({
  publisherOrganization,
  onPublisherOrganizationChange,
  searchText,
  setSearchText,
  publisherOptions,
  stateOptions,
  onStateChange,
  onAdjudicationFilterChange,
  activeFilters,
  loading,
  columnCertifiedFilter,
  setColumnCertifiedFilter
}: FastConfirmPublisherFormProps) {
  const formattedPublisherOptions = publisherOptions.map(formatOptions);
  const activeFilterCount = getLocationFilterCount(activeFilters);
  const showResults =
    !loading && (searchText || activeFilterCount > 0 || columnCertifiedFilter);

  const showExternalPublisherFilter = useBooleanFlag(
    LaunchDarklyFlags.ENABLE_EXTERNAL_PUBLISHER_FILTER,
    false
  );

  if (publisherOrganization) {
    const publisherOrganizationModel = getModelFromSnapshot(
      OrganizationModel,
      getFirebaseContext(),
      publisherOrganization
    );
    const publisherLogo = getNewspaperLogo(publisherOrganizationModel);

    return (
      <>
        <p className="text-sm text-column-gray-700 font-semibold py-2">
          Selected publisher
        </p>
        <div className="mb-4 border p-3 rounded-md">
          {publisherLogo && (
            <div>
              <img
                className="h-12 max-w-48 object-contain rounded-lg"
                src={publisherLogo}
              />
            </div>
          )}
          <div className="space-y-1">
            <h2 className="text-lg text-column-gray-700 font-semibold">
              {publisherOrganization.data().name}
              <span className="text-sm font-normal"> | Public Notices</span>
            </h2>
            <p className="text-sm font-normal">
              {getPublisherLocationDescription(publisherOrganization.data())}
            </p>
            {showExternalPublisherFilter && (
              <div>
                {publisherOrganizationModel.isExpress ? (
                  <Badge status="warning">Extended Network</Badge>
                ) : (
                  <Badge status="info">Column Certified</Badge>
                )}
              </div>
            )}
          </div>
        </div>
        <ColumnButton
          type="button"
          buttonText="Search for a different publisher"
          secondary
          onClick={() => onPublisherOrganizationChange('')}
        />
      </>
    );
  }

  const searchDescription = [
    searchText ? `"${searchText}"` : '',
    activeFilterCount > 0
      ? `(${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''})`
      : ''
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <div className="space-y-3">
        <TextField
          id="publisher-search"
          labelText="Search for publisher by name or location"
          placeholder={`Search "Express Times" or "Essex County" or "Kansas"`}
          prefix={<MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />}
          value={searchText}
          onChange={setSearchText}
        />
        <div className="flex justify-end">
          <PublisherLocationFilter
            stateOptions={stateOptions}
            onStateChange={onStateChange}
            activeFilters={activeFilters}
            onAdjudicationFilterChange={onAdjudicationFilterChange}
          />
        </div>
      </div>

      {showResults && (
        <div className="md:flex justify-between items-center my-4 space-y-2">
          <p className="text-sm text-column-gray-700 font-semibold">
            {formattedPublisherOptions.length > 0 ? (
              <>Showing results for {searchDescription}</>
            ) : (
              <>No results found for {searchDescription}</>
            )}
          </p>
          {showExternalPublisherFilter && (
            <div className="self-end">
              <CheckboxInput
                inputPosition="left"
                id="column-certified-filter"
                checked={columnCertifiedFilter}
                onChange={setColumnCertifiedFilter}
                labelText="Show only Column Certified publishers"
              />
            </div>
          )}
        </div>
      )}
      {loading && (
        <div className="p-6">
          <LoadingSpinner />
        </div>
      )}
      <div className="space-y-3">
        {showResults &&
          formattedPublisherOptions.length > 0 &&
          formattedPublisherOptions.map(publisher => (
            <div
              className="flex w-full justify-between items-end border border-column-gray-200 p-2 rounded-md space-y-2"
              key={publisher.value}
            >
              <div className="space-y-1">
                <h2 className="text-md mb-0 pb-0 text-column-gray-700 font-semibold">
                  {publisher.label}
                </h2>
                <span className="text-xs font-normal">
                  {publisher.description}
                </span>
                {showExternalPublisherFilter && (
                  <div>
                    {publisher.isExternalPublisher ? (
                      <Badge status="warning">Extended Network</Badge>
                    ) : (
                      <Badge status="info">Column Certified</Badge>
                    )}
                  </div>
                )}
              </div>
              <ColumnButton
                type="button"
                size="sm"
                buttonText="Select"
                primary
                aria-label={`Select ${publisher.label}`}
                onClick={() => onPublisherOrganizationChange(publisher.value)}
              />
            </div>
          ))}
      </div>
    </>
  );
}
